import { UseFormSetError } from 'react-hook-form'
import { logSentryError } from '@node-space/utils'
import {
  Address,
  BaseErrorResponse,
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryDetailsFiatDetails,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
  PaymentMethods,
} from 'types/beneficiaries'
import { BankAccountPaymentInstrument } from 'types/contacts'
import { SourceWallet, SourceWalletTypes } from 'types/move/move'
import { ConfirmPayeeResponse, OrchestratedConfirmPayeeName } from 'types/payoutsV2'
import { WalletType } from 'types/types'
import {
  UnifiedPayoutsBankAccount,
  UnifiedPayoutsV2Request,
} from 'types/unifiedPayouts/unifiedPayoutsV2'
import { WalletPayReceiverDetails, WalletsPayRequest } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { getBeneficiaryName } from 'utils/beneficiaries'
import track from 'utils/tracker'
import { sentenceCase } from 'utils/utils'
import { SourcePayWallet } from '../Pay/types'
import { FiatSendFormData } from './form/fiatSendFormSchema'
import { LegacyFiatPayoutBeneficiaryDetails, LegacyFiatPayoutWalletDetails } from './types'

export const PAYMENT_DETAILS_ERROR_MESSAGE_MAPPING = {
  amount: 'amount',
  currency: 'currencyCode',
  walletId: 'sourceWalletLsid',
  'beneficiary.remittanceInformation': 'paymentReference',
}

const getEntityDetails = (beneficiary: BeneficiaryDetails) => {
  const { entityType, firstName, lastName, businessName, dateOfBirth } = beneficiary || {}
  const isBusinessEntity = entityType === BeneficiaryEntityType.BUSINESS

  return {
    type: isBusinessEntity ? BeneficiaryEntityType.COMPANY : BeneficiaryEntityType.INDIVIDUAL,
    ...(isBusinessEntity
      ? {
          legalName: businessName || '',
        }
      : {
          firstName: firstName || '',
          lastName: lastName || '',
          ...(dateOfBirth && { dateOfBirth: dateOfBirth.replaceAll('/', '-') }),
        }),
    relationshipType:
      beneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY
        ? BeneficiaryType.SELF_OWNED
        : BeneficiaryType.THIRD_PARTY,
  }
}

const getAddressObject = (address?: Address) => {
  if (!address) return undefined

  return {
    ...(address?.addressLine1 && { addressLine1: address.addressLine1 }),
    ...(address?.addressLine2 && { addressLine2: address.addressLine2 }),
    ...(address?.city && { city: address.city }),
    ...(address?.region && { region: address.region }),
    ...(address?.postCode && { postCode: address.postCode }),
    ...(address?.country && { country: address.country }),
  }
}

const getBankAccountDetails = (fiat: BeneficiaryDetailsFiatDetails): UnifiedPayoutsBankAccount => {
  return {
    accountNumber: fiat?.accountNumber || '',
    ...(fiat?.accountType && { accountType: fiat.accountType }),
    bank: {
      identificationCode: fiat?.code || '',
      ...(fiat?.nid && { nid: fiat.nid }),
      ...(fiat?.bankAddress && {
        address: getAddressObject(fiat.bankAddress),
      }),
    },
    ...(fiat?.intermediaryBank && {
      intermediaryBanks: getIntermediaryBanks(fiat),
    }),
  }
}

const getIntermediaryBanks = (fiat?: BeneficiaryDetailsFiatDetails) => {
  if (!fiat?.intermediaryBank) return

  const { intermediaryBank } = fiat
  return [
    {
      identificationCode: intermediaryBank?.code || '',
      name: intermediaryBank?.bankName || '',
      ...(intermediaryBank?.nid && { nid: intermediaryBank.nid }),
      ...(intermediaryBank?.bankAddress && {
        address: getAddressObject(intermediaryBank.bankAddress),
      }),
    },
  ]
}

const getContactBankAccountBankCodes = (contactBankAccount: BankAccountPaymentInstrument) => {
  const { bank, paymentMethods } = contactBankAccount || {}
  const { identificationCode, swiftCode } = bank || {}

  const isUSD = contactBankAccount?.currency === BeneficiaryDetailsCurrency.USD
  const isUSCountry = contactBankAccount?.bank?.address?.country === 'US'
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)

  if (isUSD) {
    if (isSwift) {
      return isUSCountry
        ? {
            nid: identificationCode || '',
            identificationCode: swiftCode || '',
          }
        : {
            identificationCode: swiftCode || '',
          }
    }

    return {
      identificationCode: identificationCode || '',
    }
  }

  return {
    identificationCode,
  }
}

export const getUnifiedPayoutRequestV2WithContactBankAccount = (
  formValues: FiatSendFormData,
  selectedWallet: SourceWallet,
  contactBankAccount: BankAccountPaymentInstrument
): UnifiedPayoutsV2Request => {
  const {
    accountNumber,
    accountType,
    accountHolderName,
    accountHolderAddress,
    bank,
    intermediaryBanks,
  } = contactBankAccount || {}

  // Determine if international transfer based on destinations
  const selectedPaymentMethod = formValues?.paymentMethod
  const isInternational = contactBankAccount?.paymentMethods?.includes(PaymentMethods.SWIFT)
  // Use SWIFT for international, otherwise use the form's selected payment method
  const paymentMethod = isInternational ? PaymentMethods.SWIFT : selectedPaymentMethod
  const isUSD = contactBankAccount?.currency === BeneficiaryDetailsCurrency.USD
  const isBusinessEntity = !!accountHolderName?.legalName

  const intermediaryBank = intermediaryBanks?.[0]
  const isIntermediaryBankInUS = intermediaryBank?.address?.country === 'US'

  return {
    walletId: selectedWallet?.walletId || '',
    amount: Number(formValues?.amount),
    currency: selectedWallet?.currency || '',
    ...(paymentMethod && { method: paymentMethod }),
    beneficiary: {
      remittanceInformation: formValues?.paymentReference || '',
      entity: {
        type: isBusinessEntity ? BeneficiaryEntityType.COMPANY : BeneficiaryEntityType.INDIVIDUAL,
        ...(isBusinessEntity
          ? {
              legalName: accountHolderName?.legalName || '',
            }
          : {
              firstName: accountHolderName?.firstName || '',
              lastName: accountHolderName?.lastName || '',
            }),
        relationshipType: BeneficiaryType.THIRD_PARTY,
        ...(accountHolderAddress && {
          address: getAddressObject(accountHolderAddress),
        }),
      },
      bankAccount: {
        accountNumber: accountNumber || '',
        ...(accountType && { accountType }),
        bank: {
          ...getContactBankAccountBankCodes(contactBankAccount),
          ...(bank?.name && { name: bank.name }),
          ...(bank?.address && {
            address: getAddressObject(bank.address),
          }),
        },
        ...(intermediaryBank && {
          ...(intermediaryBank?.identificationCode && {
            identificationCode: intermediaryBank.identificationCode,
          }),
          ...(isUSD && isInternational && isIntermediaryBankInUS
            ? {
                nid: intermediaryBank.identificationCode,
                identificationCode: intermediaryBank.swiftCode,
              }
            : {
                identificationCode: intermediaryBank.identificationCode,
              }),
          ...(intermediaryBank?.name && { name: intermediaryBank.name }),
          ...(intermediaryBank?.address && {
            address: getAddressObject(intermediaryBank.address),
          }),
        }),
      },
    },
  }
}

export const getUnifiedPayoutRequestV2 = (
  formValues: FiatSendFormData,
  selectedWallet: SourceWallet,
  fiatBeneficiary: BeneficiaryDetails
): UnifiedPayoutsV2Request => {
  const { fiat, address } = fiatBeneficiary || {}
  const isSwift =
    fiatBeneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const selectedPaymentMethod = formValues?.paymentMethod || fiatBeneficiary?.paymentMethod
  const paymentMethod = isSwift ? PaymentMethods.SWIFT : selectedPaymentMethod

  return {
    walletId: selectedWallet?.walletId || '',
    amount: Number(formValues?.amount),
    currency: selectedWallet?.currency || '',
    ...(paymentMethod && {
      method: paymentMethod,
    }),
    beneficiary: {
      remittanceInformation: formValues?.paymentReference || '',
      entity: {
        ...getEntityDetails(fiatBeneficiary),
        ...(address && {
          address: getAddressObject(address),
        }),
      },
      bankAccount: getBankAccountDetails(fiat),
    },
  }
}

export const getLegacyPayoutRequest = (
  formValues: FiatSendFormData,
  selectedFiatBeneficiary: BeneficiaryDetails,
  twoFactorEnabled: boolean
): WalletsPayRequest => {
  const {
    fiat,
    address,
    addressCountry,
    entityType,
    transferDestination,
    firstName,
    lastName,
    dateOfBirth,
    businessName,
  } = selectedFiatBeneficiary || {}
  const { bankAddress, bankName, intermediaryBank, accountNumber, code } = fiat || {}

  const payRequest: WalletsPayRequest = {
    amount: Number(formValues?.amount),
    authorization: {
      ...(twoFactorEnabled && { totp: formValues.twoFactorCode }),
      ...(!twoFactorEnabled && { password: formValues.twoFactorCode }),
    },
    receiver: {
      ...(entityType && { type: entityType }),
      ...(addressCountry && { countryCode: addressCountry }),
      ...(formValues?.paymentReference && { reference: formValues?.paymentReference }),
      ...(transferDestination === BeneficiaryTransferDestination.INTERNATIONAL && {
        ...(address && { address }),
        ...(bankAddress && { bankAddress }),
        ...(intermediaryBank && { intermediaryBank }),
        ...(bankName && { bankName }),
      }),
      ...(accountNumber && { bankAccountNumber: accountNumber }),
      ...(code && { code }),
      ...(entityType === BeneficiaryEntityType.INDIVIDUAL && {
        firstName,
        lastName,
        dateOfBirth,
      }),
      ...(entityType === BeneficiaryEntityType.BUSINESS && {
        companyName: businessName,
      }),
    } as WalletPayReceiverDetails,
  }

  return payRequest
}

export const logLegacyFiatPayout = (
  wallet: LegacyFiatPayoutWalletDetails,
  beneficiary: LegacyFiatPayoutBeneficiaryDetails
) => {
  const eventName = beneficiary?.isThirdParty
    ? AmplitudeEvent.PAY_REQUEST
    : AmplitudeEvent.WITHDRAW_REQUEST

  logSentryError('Error from FiatSend - Legacy payout', {
    wallet,
    beneficiary,
  })

  track.Amp.track(eventName, {
    category: AmplitudeEventCategory.MERCHANT_PORTAL,
    action: AmplitudeEventAction.CLICK,
    version: 1,
    wallet,
    beneficiary,
  })
}

export const getConfirmationOfPayeeName = (
  beneficiary: BeneficiaryDetails,
  confirmationOfPayeeResponse: ConfirmPayeeResponse
): OrchestratedConfirmPayeeName => {
  const { actualName } = confirmationOfPayeeResponse || {}
  const { entityType } = beneficiary || {}
  const isBeneficiaryBusiness = entityType === BeneficiaryEntityType.BUSINESS

  if (!actualName) return {}

  if (isBeneficiaryBusiness) {
    return { businessName: actualName }
  }

  const names = actualName.split(' ')

  return {
    firstName: names?.[0],
    lastName: names?.slice(1).join(' '),
  }
}

export const logLegacyPayout = (
  selectedWallet: SourcePayWallet,
  fiatBeneficiary: BeneficiaryDetails,
  isLegacyPaymentMethod: boolean
) => {
  const wallet = {
    name: selectedWallet?.walletName,
    currency: selectedWallet?.currency,
    supportedTransferDestinations: selectedWallet?.supportedTransferDestinations,
    id: selectedWallet?.legacyWalletId,
    lsid: selectedWallet?.walletId,
  }

  const beneficiary = {
    name: getBeneficiaryName(fiatBeneficiary),
    alias: fiatBeneficiary?.alias,
    transferDestination: fiatBeneficiary?.transferDestination,
    currency: fiatBeneficiary?.currency,
    ...(isLegacyPaymentMethod && { id: Number(fiatBeneficiary?.reference) }),
    ...(!isLegacyPaymentMethod && { reference: fiatBeneficiary?.reference }),
    entityType: fiatBeneficiary?.entityType,
    isThirdParty: fiatBeneficiary?.beneficiaryType === BeneficiaryType.THIRD_PARTY,
  }

  logLegacyFiatPayout(wallet, beneficiary)
}

export const setPayoutErrors = ({
  feeErrorResponse,
  setError,
  setFeeErrorMessages,
}: {
  feeErrorResponse: BaseErrorResponse
  setError: UseFormSetError<FiatSendFormData>
  setFeeErrorMessages: (value: string[]) => void
}) => {
  const generalErrorMessage = feeErrorResponse?.data?.message || feeErrorResponse?.message
  if (generalErrorMessage) {
    setFeeErrorMessages([generalErrorMessage])
    return
  }

  const fieldErrors = feeErrorResponse?.data?.details?.errors || {}
  const errorsKeys = Object.keys(fieldErrors)
  const otherErrors: Record<string, string[]> = {}

  errorsKeys.forEach((errorKey: string) => {
    const isPaymentDetailsError = Object.keys(PAYMENT_DETAILS_ERROR_MESSAGE_MAPPING).includes(
      errorKey
    )

    if (isPaymentDetailsError) {
      const mappedErrorKey = PAYMENT_DETAILS_ERROR_MESSAGE_MAPPING[errorKey]

      if (mappedErrorKey) {
        const errorList = fieldErrors[errorKey]

        if (errorList) {
          const orchestratedErrorMessages = errorList?.map((error: string) => sentenceCase(error))

          setError(mappedErrorKey, { message: orchestratedErrorMessages.join('. ') })
        }
      }
    } else {
      otherErrors[errorKey] = fieldErrors[errorKey]
    }
  })

  if (Object.keys(otherErrors).length) {
    const errorMessages = Object.values(otherErrors)
      .flat()
      .map((error: string) => sentenceCase(error))
    setFeeErrorMessages(errorMessages)
  }
}

// Helper function to create SourcePayWallet from WalletType
export const createSourcePayWallet = (
  wallet: WalletType,
  walletType: (typeof SourceWalletTypes)[keyof typeof SourceWalletTypes]
): SourcePayWallet => ({
  walletId: wallet?.lsid,
  legacyWalletId: wallet?.id,
  walletName: wallet?.description,
  currency: wallet?.currency?.code,
  balance: wallet?.available,
  walletType,
  supportedTransferDestinations: wallet?.supportedTransferDestinations,
  supportsInternalBvnkNetworkTransfers: wallet?.supportsInternalBvnkNetworkTransfers,
  supportsFirstPartyPayouts: wallet?.supportsWithdrawals,
  supportsThirdPartyPayouts: wallet?.supportsThirdParty,
  supportsPayins: wallet?.supportsDeposits,
})

// Helper function to get payment methods based on wallet currency and destinations
export const getPaymentMethodsForCurrency = (
  currency: string,
  supportedTransferDestinations?: BeneficiaryTransferDestination[]
): Array<(typeof PaymentMethods)[keyof typeof PaymentMethods]> => {
  const isSwiftWalletOnly =
    supportedTransferDestinations?.includes(BeneficiaryTransferDestination.INTERNATIONAL) &&
    supportedTransferDestinations?.length === 1

  if (isSwiftWalletOnly) {
    return [PaymentMethods.SWIFT]
  }

  const paymentMethods: Array<(typeof PaymentMethods)[keyof typeof PaymentMethods]> = []

  switch (currency) {
    case 'USD':
      paymentMethods.push(
        PaymentMethods.ACH,
        PaymentMethods.FEDWIRE,
        PaymentMethods.CRNOW,
        PaymentMethods.CUBIX,
        PaymentMethods.FEDNOW,
        PaymentMethods.RTP
      )
      break
    case 'EUR':
      paymentMethods.push(PaymentMethods.SEPA, PaymentMethods.SEPA_INSTANT)
      break
    case 'GBP':
      paymentMethods.push(PaymentMethods.FASTER_PAYMENT)
      break
    default:
      break
  }

  if (
    supportedTransferDestinations &&
    supportedTransferDestinations.length > 1 &&
    supportedTransferDestinations.includes(BeneficiaryTransferDestination.INTERNATIONAL)
  ) {
    paymentMethods.push(PaymentMethods.SWIFT)
  }

  return paymentMethods
}
